import { showDialogInfo } from "@/hooks/use-dialog-info"
import { UserPresenceStatus, UserPresenceType } from "@/types/presence"
import {
  CollaboratorActiveEventData,
  CollaboratorEventData,
  TripEventAdviceUpdatedData,
  TripEventChatBecameGroupChat,
  TripEventChatMovedData,
  TripEventCollaboratorAddedData,
  TripEventCollaboratorRemovedData,
  TripEventData,
  TripEventItineraryReviewData,
  TripEventMessageChangedData,
  TripEventMessageDeletedData,
  TripEventPresenceChangedData,
} from "@/types/socket"
import { UserRef } from "@/types/user"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { buildClientAxios } from "@/lib/client-axios"
import { assertPresent, warnUnreachable } from "@/lib/utils/assertions"
import { ChatApi } from "../chat-api"
import { now } from "../date-utils"
import { SocketManager } from "../socket-manager"
import { assistantSuggestionsApi } from "./api/assistant-suggestions-api"
import { chatsApi } from "./api/chats-api"
import { detectedTripsApi } from "./api/detected-trips-api"
import { needsAttentionApi } from "./api/needs-attention-api"
import {
  addCollaboratorToCache,
  removeCollaboratorFromCache,
  tripsApi,
  updateCollaboratorsCache,
  updateTripCache,
} from "./api/trips-api"
import { performBotAction } from "./bot-action-thunks"
import { exitCurrentTripChat } from "./chat-exit-thunks"
import {
  fetchNewMessages,
  fetchNewTripLogItems,
  loadChatTrip,
} from "./chat-thunks"
import {
  addPendingBotResponse,
  removePendingBotResponse,
  selectOtherUsersWhoAreTyping,
  selectPendingBotResponse,
  selectRefsOfUsersWhoArePresent,
  updateUserPresence,
} from "./presence-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripId, selectTripRef, setTripRef } from "./trip-slice"
import userSlice, {
  selectChatId,
  selectCurrentUserRef,
  selectPersistentMessages,
  selectStoppedStreamIds,
  selectUserIsAnonymous,
} from "./user-slice"

// A backstop for a response whose end broadcast never arrives (e.g. a dropped
// socket); long enough for a full itinerary generation.
const PENDING_BOT_RESPONSE_TIMEOUT_MS = 10 * 60 * 1000

// An event without a chat_id came from a server that predates trips having
// several group chats, so it applies to whichever chat is current.
function isCurrentChat(chatId: number | undefined, state: RootState) {
  return chatId === undefined || chatId === selectChatId(state)
}

export const announcePresenceChange = createAsyncThunk<
  void,
  { presenceType: UserPresenceType },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "collaborationThunks/announcePresenceChange",
  async ({ presenceType }, { dispatch, getState }) => {
    if (!SocketManager.hasInstance) return
    if (selectUserIsAnonymous(getState())) return

    const userIsAnonymous = selectUserIsAnonymous(getState())
    if (userIsAnonymous) return

    const tripId = selectTripId(getState())
    if (!tripId) return

    const userRef = assertPresent(selectCurrentUserRef(getState()))
    SocketManager.instance.announcePresenceChange(tripId, presenceType)
    dispatch(
      updateUserPresence({
        user_ref: userRef,
        presence_type: presenceType,
        timestamp: now(),
      })
    )
  }
)

export const receiveCollaboratorEvent = createAsyncThunk<
  void,
  { userRef: UserRef; data: CollaboratorEventData },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "collaborationThunks/receiveCollaboratorEvent",
  async ({ userRef, data }, { dispatch, getState }) => {
    const type = data.type
    switch (type) {
      case "active":
        await receiveCollaboratorActive(userRef, data, dispatch, getState)
        break
      case "updated":
        // TODO
        break
      default:
        warnUnreachable(type)
    }
  }
)

export const receiveTripEvent = createAsyncThunk<
  void,
  { data: TripEventData },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "collaborationThunks/receiveTripEvent",
  async ({ data }, { dispatch, getState }) => {
    const type = data.type
    switch (type) {
      case "change":
        void dispatch(
          performBotAction({
            action: { type: "trip_change", trip_change: data.change },
          })
        )
        break
      case "advice_updated":
        await receiveAdviceUpdated(data, dispatch, getState)
        break
      case "needs_attention_item_updated":
        receiveNeedsAttentionItemUpdated(dispatch)
        break
      case "found_bookings":
        receiveFoundBookings(dispatch)
        break
      case "itinerary_review":
        await receiveItineraryReviewUpdated(data, dispatch, getState)
        break
      case "message_changed":
        await updateChatMessage(data, dispatch, getState)
        break
      case "message_deleted":
        removeChatMessage(data, dispatch, getState)
        break
      case "chat_moved":
        await receiveChatMoved(data, dispatch, getState)
        break
      case "chat_became_group_chat":
        await receiveChatBecameGroupChat(data, dispatch, getState)
        break
      case "collaborator_added":
        await receiveCollaboratorAdded(data, dispatch, getState)
        break
      case "collaborator_removed":
        await receiveCollaboratorRemoved(data, dispatch, getState)
        break
      case "presence_changed":
        await receivePresenceChanged(data, dispatch, getState)
        break
      case "group_chat":
        if (isCurrentChat(data.chat_id, getState())) {
          await dispatch(fetchNewMessages())
        }
        break
      case "group_chat_begin_streaming_bot_response": {
        const streamId = data.stream_id
        dispatch(
          addPendingBotResponse({
            chat_id: data.chat_id,
            stream_id: streamId,
            timestamp: now(),
            responding_to_user_ref: data.responding_to_user_ref,
          })
        )
        setTimeout(
          () => dispatch(removePendingBotResponse(streamId)),
          PENDING_BOT_RESPONSE_TIMEOUT_MS
        )
        break
      }
      case "group_chat_end_streaming_bot_response": {
        const state = getState()
        const streamId = data.stream_id
        const inCurrentChat = isCurrentChat(data.chat_id, state)
        if (!data.bot_message_id) {
          const pending = selectPendingBotResponse(state, streamId)
          if (
            inCurrentChat &&
            pending?.responding_to_user_ref === selectCurrentUserRef(state) &&
            !selectStoppedStreamIds(state).includes(streamId)
          ) {
            dispatch(userSlice.actions.setGroupResponseFailed(true))
          }
          dispatch(removePendingBotResponse(streamId))
        } else if (
          !inCurrentChat ||
          selectPersistentMessages(state).some(
            (m) => m.id === data.bot_message_id
          )
        ) {
          dispatch(removePendingBotResponse(streamId))
        }
        // Otherwise the indicator stays up until the reply itself is fetched,
        // which removes it.
        break
      }
      default:
        warnUnreachable(type)
    }
  }
)

const updateChatMessage = async (
  { chat_id, message }: TripEventMessageChangedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const activeChatId = selectChatId(getState())
  if (chat_id !== activeChatId) return
  dispatch(userSlice.actions.addOrUpdateMessages({ messages: [message] }))
}

const removeChatMessage = (
  { chat_id, message_id }: TripEventMessageDeletedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const activeChatId = selectChatId(getState())
  if (chat_id !== activeChatId) return
  dispatch(userSlice.actions.removeMessage({ messageId: message_id }))
}

// Routed bookings change the trip's pending card and the hub badge for everyone on the trip.
const receiveFoundBookings = (dispatch: AppDispatch) => {
  void dispatch(detectedTripsApi.util.invalidateTags(["DetectedTripReview"]))
}

// A row settling also opens or closes the trip's advice — Trip#disrupted? gates it — and
// both lists are keyed by user rather than by the open trip, so both are re-read.
const receiveNeedsAttentionItemUpdated = (dispatch: AppDispatch) => {
  void dispatch(needsAttentionApi.util.invalidateTags(["NeedsAttention"]))
  void dispatch(tripsApi.util.invalidateTags(["TripAdvice"]))
  void dispatch(
    assistantSuggestionsApi.util.invalidateTags(["AssistantSuggestions"])
  )
}

const receiveAdviceUpdated = async (
  data: TripEventAdviceUpdatedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const tripRef = selectTripRef(getState())
  if (!tripRef) return

  void dispatch(
    tripsApi.util.upsertQueryData(
      "getTripAdvices",
      tripRef,
      data.advices_response
    )
  )
}

const receiveItineraryReviewUpdated = async (
  data: TripEventItineraryReviewData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const tripRef = selectTripRef(getState())
  if (!tripRef) return

  void dispatch(
    tripsApi.util.upsertQueryData(
      "getItineraryReview",
      tripRef,
      data.review_response
    )
  )
}

const receiveCollaboratorAdded = async (
  data: TripEventCollaboratorAddedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const tripRef = selectTripRef(getState())
  if (!tripRef) return
  addCollaboratorToCache(dispatch, tripRef, data.collaborator)
  updateTripCache(dispatch, tripRef, { collaboration: true })
  dispatch(
    tripsApi.util.invalidateTags([
      { type: "Collaborators", id: tripRef },
      { type: "Trip", id: tripRef },
    ])
  )
  dispatch(chatsApi.util.invalidateTags([{ type: "Chats", id: tripRef }]))
  void dispatch(fetchNewTripLogItems())
}

// The server moves a chat onto a saved trip for voice, text or the chat menu,
// sometimes mid-response. updateChat keeps the in-progress message that setChat
// would clear, so the trip slice is pointed at the new trip separately.
const receiveChatMoved = async (
  data: TripEventChatMovedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const chatId = data.chat_id
  dispatch(chatsApi.util.invalidateTags(["Chats"]))
  if (chatId !== selectChatId(getState())) {
    return
  }

  const chat = await new ChatApi(buildClientAxios()).fetchChat(chatId)
  dispatch(
    tripsApi.util.invalidateTags([{ type: "TripCounts", id: chat.trip.ref }])
  )
  // updateChat asserts its id is the open chat, which can change during the fetch.
  if (chatId !== selectChatId(getState())) {
    return
  }
  dispatch(userSlice.actions.updateChat({ id: chatId, trip: chat.trip }))
  dispatch(setTripRef(chat.trip.ref))
  loadChatTrip(dispatch, chat.trip)
}

const receiveChatBecameGroupChat = async (
  data: TripEventChatBecameGroupChat,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const chatId = data.chat_id
  dispatch(chatsApi.util.invalidateTags(["Chats"]))
  if (chatId !== selectChatId(getState())) {
    return
  }

  const chat = await new ChatApi(buildClientAxios()).fetchChat(chatId)
  // updateChat asserts its id is the open chat, which can change during the fetch.
  if (chatId !== selectChatId(getState())) {
    return
  }
  // Only the fields a conversion changes: the open chat's messages and stream ids keep changing during the fetch
  dispatch(
    userSlice.actions.updateChat({
      id: chatId,
      group: chat.group,
      memory_enabled: chat.memory_enabled,
      memory_setting_editable: chat.memory_setting_editable,
    })
  )
}

const receiveCollaboratorRemoved = async (
  data: TripEventCollaboratorRemovedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const currentUserRef = selectCurrentUserRef(getState())
  const tripRef = selectTripRef(getState())
  if (!tripRef) return
  const refresh = async () => {
    if (data.user_ref === currentUserRef) {
      await dispatch(exitCurrentTripChat()).unwrap()
    } else {
      removeCollaboratorFromCache(dispatch, tripRef, data.user_ref)
      const remainingCollaborators =
        tripsApi.endpoints.getCollaborators.select(tripRef)(getState()).data ??
        []
      if (remainingCollaborators.length <= 1) {
        updateTripCache(dispatch, tripRef, { collaboration: false })
      }
      dispatch(
        tripsApi.util.invalidateTags([
          { type: "Collaborators", id: tripRef },
          { type: "Trip", id: tripRef },
        ])
      )
      void dispatch(fetchNewTripLogItems())
    }
  }
  if (
    data.user_ref === currentUserRef &&
    data.removed_by_user_ref !== currentUserRef
  ) {
    showDialogInfo({
      title: "Removed from trip",
      message: "You have been removed from this trip.",
      okLabel: "OK",
      okAction: () => void refresh(),
      cancelAction: () => void refresh(),
    })
  } else {
    await refresh()
  }
}

const receivePresenceChanged = async (
  data: TripEventPresenceChangedData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const timestamp = now()
  const status: UserPresenceStatus = {
    user_ref: data.user_ref,
    presence_type: data.presence_type,
    timestamp,
  }
  dispatch(updateUserPresence(status))

  const tripRef = selectTripRef(getState())
  const userRefs = selectRefsOfUsersWhoArePresent(getState())
  if (tripRef) {
    updateCollaboratorsCache(dispatch, tripRef, userRefs)
  }
}

const receiveCollaboratorActive = async (
  userRef: UserRef,
  _data: CollaboratorActiveEventData,
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  // If this user is already "typing", don't override that with "active"
  if (
    selectOtherUsersWhoAreTyping(getState()).find((u) => u.user_ref === userRef)
  )
    return

  const timestamp = now()
  const status: UserPresenceStatus = {
    user_ref: userRef,
    presence_type: "active",
    timestamp,
  }
  dispatch(updateUserPresence(status))

  const tripRef = selectTripRef(getState())
  const userRefs = selectRefsOfUsersWhoArePresent(getState())
  if (tripRef) {
    updateCollaboratorsCache(dispatch, tripRef, userRefs)
  }
}
